import { Typography } from "@material-ui/core";
import { Chart, ChartDataset } from "chart.js";
import { formatTempItemsForChart } from "helpers/tempProfileChartHelpers";
import { theme } from "styling/theme";
import { constructOptions, plugins } from "./config";
import { TempProfileChartProps } from "./TempProfileChartProps";
import { tempProfileChartStyles } from "./TempProfileChartStyles";
import zoomPlugin from "chartjs-plugin-zoom";
import "chartjs-adapter-moment";
import { useEffect, useRef, useState } from "react";
import moment from "moment";
import LoadingIndicator from "components/LoadingIndicator/LoadingIndicator";

Chart.register(zoomPlugin);

export function TempProfileChart({
    tempItems,
    minTimestamp,
    maxTimestamp,
    isLoading = false,
}: TempProfileChartProps) {
    const classes = tempProfileChartStyles();

    const formattedData = formatTempItemsForChart(tempItems);

    const datasets: ChartDataset<"line">[] = [
        {
            label: "max temp",
            data: formattedData.maxTempData,
            borderColor: theme.colors.primary.blue600,
            borderCapStyle: "round",
        },
        {
            label: "mean temp",
            data: formattedData.meanTempData,
            borderColor: theme.colors.feedback.warning,
            borderCapStyle: "round",
            borderDash: [6, 6],
        },
        {
            label: "min temp",
            data: formattedData.minTempData,
            borderColor: theme.colors.neutrals.neutral400,
            borderCapStyle: "round",
        },
    ];

    const divRef = useRef<HTMLCanvasElement | null>(null);
    const [chart, setChart] = useState<Chart>();

    useEffect(() => {
        if (divRef.current === null) {
            return;
        }

        const chartInstance = new Chart(divRef.current, {
            type: "line",
            data: { datasets },
            plugins,
            options: constructOptions({
                tempItems,
                minTimestamp,
                maxTimestamp,
            }),
        });
        chartInstance.update();
        setChart(chartInstance);

        return () => chartInstance.destroy();
    }, [tempItems]);

    useEffect(() => {
        if (chart && !isNaN(minTimestamp) && !isNaN(maxTimestamp)) {
            const from = minTimestamp;
            let to = maxTimestamp;

            if (minTimestamp === maxTimestamp) {
                to = from + 60 * 60 * 24 * 1000 - 1;
            }

            const inRange = formatTempItemsForChart(
                tempItems.filter((x) => {
                    return (
                        moment(x.timeStamp).valueOf() >= from &&
                        moment(x.timeStamp).valueOf() <= to
                    );
                })
            );

            chart.zoomScale("x", { min: from, max: to });

            if (
                inRange.minTempData.length === 0 ||
                inRange.maxTempData.length === 0
            ) {
                return;
            }

            const min = inRange.minTempData.reduce(
                (a, b) => (a.y > b.y ? b : a),
                inRange.minTempData[0]
            ).y;

            const max = inRange.maxTempData.reduce(
                (a, b) => (a.y > b.y ? a : b),
                inRange.maxTempData[0]
            ).y;

            chart.zoomScale("y", { min, max });
        }
    }, [minTimestamp, maxTimestamp]);

    return (
        <div>
            <Typography variant="h4" component="h4" className={classes.title}>
                Lane Temperature Profile
            </Typography>
            <div className={classes.container}>
                <canvas ref={divRef} />
                {isLoading && (
                    <div className={classes.indicatorContainer}>
                        <LoadingIndicator
                            className={classes.indicator}
                            isDimmed
                        />
                    </div>
                )}
            </div>
        </div>
    );
}
